/**
 * useLobbyMessageQueue — Per-room progressive reveal queue for lobby chat.
 *
 * Mirrors useMessageQueue but scoped to a specific lobby chat room.
 * When the current reveal finishes, waits delayBetweenMessagesMs then advances.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { MESSAGE_REVEAL_CONFIG } from '@/lib/constants/message-reveal';

const EMPTY_QUEUE: string[] = [];

export function useLobbyMessageQueue(roomId: string) {
  const revealQueue = useLobbyStore((s) => s.revealQueues[roomId] ?? EMPTY_QUEUE);
  const revealingMessageId = useLobbyStore((s) => s.revealingMessageIds[roomId] ?? null);
  // For live NPC streams the revealing message stays `isStreaming: true` until
  // NpcStreamEnd lands — sometimes >5.5s of WS chunks. The stuck-reveal
  // watchdog must NOT fire while the wire is still active, or it auto-flips
  // the message out of typewriter mode mid-stream. We arm the watchdog only
  // after `isStreaming` flips false (canned reveals never flip it false but
  // they're hard-capped at maxAnimationMs by ProgressiveReveal's rAF, so
  // they don't need the safety net).
  const revealingMessageIsStreaming = useLobbyStore((s) => {
    const id = s.revealingMessageIds[roomId];
    if (!id) return false;
    const room = s.chatRooms.find((r) => r.id === roomId);
    return room?.messages.find((m) => m.id === id)?.isStreaming ?? false;
  });
  // Distinct from `isStreaming`: only set for the head of a real WS stream
  // (NpcStreamStart). Used by destructive operations (ArrowDown, Escape,
  // tab-switch cleanup) so canned typewriter reveals can be skipped freely
  // while live WS streams stay protected — both share `isStreaming: true`
  // and `isQueuedReveal: true`, so we can't gate on those.
  const revealingMessageIsLiveStream = useLobbyStore((s) => {
    const id = s.revealingMessageIds[roomId];
    if (!id) return false;
    const room = s.chatRooms.find((r) => r.id === roomId);
    return room?.messages.find((m) => m.id === id)?.isLiveStream ?? false;
  });
  const advanceRevealQueue = useLobbyStore((s) => s.advanceRevealQueue);
  const finishReveal = useLobbyStore((s) => s.finishReveal);
  const skipRevealQueue = useLobbyStore((s) => s.skipRevealQueue);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When no message is currently revealing and the queue has items, advance after delay
  useEffect(() => {
    if (revealingMessageId !== null || revealQueue.length === 0) return;

    timerRef.current = setTimeout(() => {
      advanceRevealQueue(roomId);
    }, MESSAGE_REVEAL_CONFIG.delayBetweenMessagesMs);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [revealingMessageId, revealQueue.length, advanceRevealQueue, roomId]);

  // Safety: auto-finish stuck reveals. Only armed once the WS stream has
  // ended (revealingMessageIsStreaming=false) — otherwise it would trip
  // mid-stream on long Aldric/Kira responses and drop them out of
  // typewriter mode. Canned-queue reveals never flip isStreaming false,
  // but they're bounded by ProgressiveReveal's maxAnimationMs rAF cap so
  // they can't truly stall.
  useEffect(() => {
    if (revealingMessageId === null || revealingMessageIsStreaming) {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
      return;
    }
    const stuckId = revealingMessageId;
    stuckTimerRef.current = setTimeout(() => {
      finishReveal(roomId, stuckId);
    }, MESSAGE_REVEAL_CONFIG.maxAnimationMs + 1000);

    return () => {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
    };
  }, [revealingMessageId, revealingMessageIsStreaming, finishReveal, roomId]);

  const skipQueue = useCallback(() => {
    skipRevealQueue(roomId);
  }, [skipRevealQueue, roomId]);

  // Per-message completion. Each ProgressiveReveal gets a closure that
  // knows its OWN message id so finishReveal can't be misdirected when
  // two reveals run concurrently (e.g., a queued Tavian message mid-
  // reveal while an NPC stream-start fires and registers a second
  // `revealingMessageIds[roomId]`). The previous shared-ref pattern
  // read the latest revealingMessageId at completion time, so whichever
  // bubble finished first would call finishReveal on the OTHER bubble's
  // id and orphan its `isQueuedReveal: true`.
  const finishRevealForRoom = useCallback(
    (messageId: string) => {
      finishReveal(roomId, messageId);
    },
    [finishReveal, roomId]
  );

  return {
    isQueueActive: revealQueue.length > 0 || revealingMessageId !== null,
    revealingMessageId,
    /** True while the revealing message is marked `isStreaming` — used by the
     *  watchdog to defer the safety-finish until the WS stream has ended. */
    revealingMessageIsStreaming,
    /** True iff the revealing message is the head of a real WS live stream.
     *  Callers use this to gate destructive operations (ArrowDown, Escape,
     *  tab-switch cleanup) so the live stream survives intact. */
    revealingMessageIsLiveStream,
    skipQueue,
    finishReveal: finishRevealForRoom,
  };
}
